import { useState, useEffect, useRef } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/Card';
import { cn } from '../../utils/cn';
import { NewsItem } from '../../types/news';

interface BoardMessageProps {
  pinnedMessage: NewsItem | null;
  className?: string;
}

export function BoardMessage({ pinnedMessage, className }: BoardMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setNeedsExpansion(contentHeight > 140);
    }
  }, [pinnedMessage]);

  useEffect(() => {
    if (pinnedMessage) {
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pinnedMessage]);

  if (!pinnedMessage) {
    return (
      <Card className={cn('w-full bg-light-3 border-condo-dark/50', className)}>
        <CardHeader>
          <CardTitle icon={AlertCircle} size="lg">
            Melding fra styret
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm sm:text-base text-gray-600 text-center py-6">
            Ingen pinnet melding fra styret for øyeblikket.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      'w-full bg-light-3 border-condo-dark/50',
      shouldAnimate && 'animate-pinned-news',
      className
    )} aria-labelledby="board-message-heading">
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <CardTitle icon={AlertCircle} size="lg" id="board-message-heading">
            Melding fra styret
          </CardTitle>
          <div className="flex items-center gap-2">
            <span 
              className={cn(
                'px-2 py-0.5 text-xs sm:text-sm font-medium',
                'bg-condo-dark text-condo-light rounded-full'
              )}
              role="status"
            >
              Viktig
            </span>
            <Tooltip content={
              <div className="whitespace-pre-line">
                Styret kan enkelt feste viktige beskjeder alle beboerne bør se.
                {'\n\n'}
                Meldingen velger du fra 'Sist Nytt' seksjonen under. Trykk pin-ikonet for å feste.
              </div>
            }>
              <Info 
                className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors cursor-help" 
                aria-label="Mer informasjon om pinnefunksjonen"
              />
            </Tooltip>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div 
          className={cn(
            'relative',
            !isExpanded && needsExpansion && 'max-h-[7em] overflow-hidden'
          )}
          id="message-content"
        >
          <div ref={contentRef} className="text-sm sm:text-base text-gray-900">
            <strong className="block mb-2">{pinnedMessage.title}</strong>
            <div className="whitespace-pre-line">{pinnedMessage.content}</div>
          </div>
          {!isExpanded && needsExpansion && (
            <div 
              className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-light-2 to-transparent"
              aria-hidden="true"
            />
          )}
        </div>

        <div className="flex justify-end mt-3">
          <time 
            className="text-xs text-gray-500"
            dateTime={pinnedMessage.date}
          >
            {pinnedMessage.date}
          </time>
        </div>
      </CardContent>

      {needsExpansion && (
        <div className="border-t border-gray-200">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "w-full p-3 text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2",
              "focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2"
            )}
            aria-expanded={isExpanded}
            aria-controls="message-content"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
                <span>Vis mindre</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                <span>Vis mer</span>
              </>
            )}
          </button>
        </div>
      )}
    </Card>
  );
}
